<template>
	<div
		v-show="state.open"
		class="walter-mapviewer"
		@click.self="close"
	>
		<div
			ref="panel"
			class="walter-mapviewer__panel"
		>
			<button
				type="button"
				class="walter-mapviewer__close"
				@click="close"
			>
				✕ {{ $t('walteriaClose') }}
			</button>
			<div
				ref="mapContainer"
				class="walter-mapviewer__canvas"
			></div>
			<div
				v-if="metricInfo"
				class="walter-mapviewer__legend"
			>
				<div class="walter-mapviewer__legend-title">
					{{ metricInfo.metric || $t('walteriaMetric')
					}}<span v-if="metricInfo.unit"> ({{ metricInfo.unit }})</span>
				</div>
				<div class="walter-mapviewer__legend-bar"></div>
				<div class="walter-mapviewer__legend-scale">
					<span>{{ formatLegendValue(metricInfo.min) }}</span>
					<span>{{ formatLegendValue(metricInfo.max) }}</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { mapViewerState, closeMapViewer } from './mapViewerState'

const CHOROPLETH_COLORS = ['#dc2626', '#f97316', '#eab308', '#16a34a', '#15803d']

function quantile(sorted, q) {
	if (sorted.length === 1) return sorted[0]
	const pos = (sorted.length - 1) * q
	const base = Math.floor(pos)
	const rest = pos - base
	const next = sorted[base + 1] != null ? sorted[base + 1] : sorted[base]
	return sorted[base] + (next - sorted[base]) * rest
}

function hexToRgb(hex) {
	const h = hex.replace('#', '')
	return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]
}

function rgbToHex(rgb) {
	const to = (n) => Math.round(n).toString(16).padStart(2, '0')
	return '#' + to(rgb[0]) + to(rgb[1]) + to(rgb[2])
}

function colorForValue(stops, v) {
	if (v <= stops[0][0]) return stops[0][1]
	const last = stops[stops.length - 1]
	if (v >= last[0]) return last[1]
	for (let i = 0; i < stops.length - 1; i++) {
		const [v0, c0] = stops[i]
		const [v1, c1] = stops[i + 1]
		if (v >= v0 && v <= v1) {
			const t = v1 === v0 ? 0 : (v - v0) / (v1 - v0)
			const a = hexToRgb(c0)
			const b = hexToRgb(c1)
			return rgbToHex([
				a[0] + (b[0] - a[0]) * t,
				a[1] + (b[1] - a[1]) * t,
				a[2] + (b[2] - a[2]) * t,
			])
		}
	}
	return last[1]
}

function getColorStops(features) {
	const values = features
		.filter((f) => f.properties && f.properties.tipo === 'talhao' && f.properties._value != null)
		.map((f) => Number(f.properties._value))
		.filter((v) => !isNaN(v))
	if (values.length === 0) return null
	const sorted = [...values].sort((a, b) => a - b)
	const min = sorted[0]
	const max = sorted[sorted.length - 1]
	if (min === max) return [[min, '#16a34a']]
	const qs = [0, 0.25, 0.5, 0.75, 1]
	const stops = []
	let prev = -Infinity
	for (let i = 0; i < qs.length; i++) {
		let v = quantile(sorted, qs[i])
		if (v <= prev) v = prev + 1e-6
		prev = v
		stops.push([v, CHOROPLETH_COLORS[i]])
	}
	return stops
}

function boundsOf(geojson) {
	const bounds = new mapboxgl.LngLatBounds()
	const addCoords = (coords) => {
		if (typeof coords[0] === 'number') bounds.extend(coords)
		else coords.forEach(addCoords)
	}
	const feats = geojson.features || [geojson]
	feats.forEach((f) => {
		try {
			addCoords(f.geometry.coordinates)
		} catch {
			/* ignore feature sem geometria */
		}
	})
	return bounds
}

export default {
	name: 'MapViewer',
	data() {
		return {
			state: mapViewerState,
			map: null,
			ready: false,
			layersAdded: false,
			popup: null,
		}
	},
	computed: {
		metricInfo() {
			const feats = (this.state.geojson && this.state.geojson.features) || []
			const values = feats
				.filter(
					(f) => f.properties && f.properties.tipo === 'talhao' && f.properties._value != null,
				)
				.map((f) => Number(f.properties._value))
				.filter((v) => !isNaN(v))
			if (values.length === 0) return null
			const withMeta = feats.find(
				(f) => f.properties && (f.properties._metric || f.properties._unit),
			)
			return {
				metric: (withMeta && withMeta.properties._metric) || '',
				unit: (withMeta && withMeta.properties._unit) || '',
				min: Math.min(...values),
				max: Math.max(...values),
			}
		},
	},
	watch: {
		'state.seq'() {
			if (this.state.open) this.show()
		},
		'state.open'(v) {
			if (v) this.show()
			else document.body.style.overflow = ''
		},
	},
	mounted() {
		window.addEventListener('keydown', this.onKeydown)
	},
	beforeDestroy() {
		window.removeEventListener('keydown', this.onKeydown)
		if (this.map) {
			this.map.remove()
			this.map = null
		}
	},
	methods: {
		onKeydown(e) {
			if (e.key !== 'Escape' || !this.state.open) return
			// Se está em tela cheia, deixa o navegador APENAS sair do fullscreen —
			// não fecha o modal (fechar enquanto o elemento fullscreen some trava o
			// navegador).
			if (this.isFullscreen()) return
			this.close()
		},
		isFullscreen() {
			return !!(document.fullscreenElement || document.webkitFullscreenElement)
		},
		exitFullscreen() {
			try {
				if (document.exitFullscreen) return document.exitFullscreen()
				if (document.webkitExitFullscreen) return document.webkitExitFullscreen()
			} catch {
				/* ignore */
			}
			return Promise.resolve()
		},
		close() {
			// remove popup aberto (evita aria-hidden sobre elemento com foco) e sai do
			// fullscreen ANTES de esconder o modal (senão o navegador trava)
			if (this.popup) {
				this.popup.remove()
				this.popup = null
			}
			if (this.isFullscreen()) {
				Promise.resolve(this.exitFullscreen()).finally(() => closeMapViewer())
				return
			}
			closeMapViewer()
		},
		formatLegendValue(v) {
			return (Math.round(Number(v) * 10) / 10).toLocaleString('pt-BR')
		},
		show() {
			document.body.style.overflow = 'hidden'
			this.$nextTick(() => {
				if (!this.map) {
					this.createMap()
				} else {
					this.updateData()
					this.map.resize()
					setTimeout(() => {
						if (this.map) this.map.resize()
					}, 100)
				}
			})
		},
		createMap() {
			const container = this.$refs.mapContainer
			if (!container || container.clientWidth === 0 || container.clientHeight === 0) {
				setTimeout(() => this.createMap(), 120)
				return
			}
			mapboxgl.accessToken =
				'pk.eyJ1IjoibWJlcmJlcnQiLCJhIjoiY2xkMXEzbTNnMDEzOTN0bXJyNHVsNm9uNiJ9.mX2Sdt4KN3Ydg3VBhsY2Sw'

			let center = [-47, -15]
			let zoom = 4
			try {
				const b = boundsOf(this.state.geojson)
				center = b.getCenter().toArray()
				zoom = 10
			} catch {
				/* defaults */
			}

			this.map = new mapboxgl.Map({
				container,
				style: 'mapbox://styles/mapbox/satellite-streets-v12',
				center,
				zoom,
			})
			this.map.addControl(new mapboxgl.NavigationControl(), 'top-left')
			// Tela cheia nativa: fullscreen do PAINEL inteiro (mantém legenda e o botão
			// de fechar visíveis). Reaproveita a mesma instância — sem criar outra.
			this.map.addControl(
				new mapboxgl.FullscreenControl({ container: this.$refs.panel }),
				'top-left',
			)

			this.map.on('load', () => {
				this.ready = true
				this.updateData()
			})
			// setData/addLayer só depois de 'load' (nunca síncrono).
		},
		updateData() {
			if (!this.map || !this.ready || !this.state.geojson) return
			const features = this.state.geojson.features || []
			const colorStops = getColorStops(features)

			const sourceData = colorStops
				? {
						type: 'FeatureCollection',
						features: features.map((f) => {
							const p = f.properties || {}
							const v = p.tipo === 'talhao' && p._value != null ? Number(p._value) : NaN
							if (!Number.isFinite(v)) return f
							return {
								...f,
								properties: {
									...p,
									_fillColor: colorForValue(colorStops, v),
									_valueLabel: String(Math.round(v)),
								},
							}
						}),
					}
				: this.state.geojson

			if (this.layersAdded && this.map.getSource('geojson-source')) {
				this.map.getSource('geojson-source').setData(sourceData)
			} else {
				this.map.addSource('geojson-source', { type: 'geojson', data: sourceData })
				this.map.addLayer({
					id: 'geojson-fill',
					type: 'fill',
					source: 'geojson-source',
					filter: ['has', '_fillColor'],
					paint: { 'fill-color': ['get', '_fillColor'], 'fill-opacity': 0.6 },
				})
				this.map.addLayer({
					id: 'geojson-fill-neutral',
					type: 'fill',
					source: 'geojson-source',
					filter: ['all', ['==', ['get', 'tipo'], 'talhao'], ['!', ['has', '_fillColor']]],
					paint: { 'fill-color': '#9ca3af', 'fill-opacity': 0.3 },
				})
				this.map.addLayer({
					id: 'geojson-labels',
					type: 'symbol',
					source: 'geojson-source',
					filter: ['has', '_fillColor'],
					layout: {
						'text-field': ['concat', ['get', 'nome'], '\n', ['get', '_valueLabel']],
						'text-size': 11,
						'text-allow-overlap': false,
					},
					paint: {
						'text-color': '#ffffff',
						'text-halo-color': '#000000',
						'text-halo-width': 1,
					},
				})
				this.map.addLayer({
					id: 'geojson-outline',
					type: 'line',
					source: 'geojson-source',
					paint: { 'line-color': '#ffffff', 'line-width': 1 },
				})

				this.map.on('click', 'geojson-fill', (e) => {
					if (!e.features || !e.features.length) return
					const props = e.features[0].properties
					// Monta o conteúdo como nós de texto e entrega via setDOMContent. As chaves e os
					// valores vêm do GeoJSON servido pela API (dados do banco do cliente, com a métrica
					// escolhida pelo LLM); concatenar isso em HTML para setHTML permitia injeção.
					const container = document.createElement('div')
					container.style.fontSize = '12px'
					container.style.maxWidth = '200px'
					const addLinha = (rotulo, valor) => {
						const linha = document.createElement('div')
						const forte = document.createElement('strong')
						forte.textContent = rotulo + ':'
						linha.appendChild(forte)
						linha.appendChild(document.createTextNode(' ' + valor))
						container.appendChild(linha)
					}
					for (const [key, val] of Object.entries(props)) {
						if (key.startsWith('_')) continue
						addLinha(key, val)
					}
					if (props._value != null) {
						addLinha(
							props._metric || '',
							Number(props._value).toFixed(1) + ' ' + (props._unit || ''),
						)
					}
					if (this.popup) this.popup.remove()
					// focusAfterOpen:false → o botão de fechar do popup não rouba o foco
					// (evita o aviso/bloqueio de aria-hidden sobre elemento focado)
					this.popup = new mapboxgl.Popup({ focusAfterOpen: false })
						.setLngLat(e.lngLat)
						.setDOMContent(container)
						.addTo(this.map)
				})
				this.map.on('mouseenter', 'geojson-fill', () => {
					if (this.map) this.map.getCanvas().style.cursor = 'pointer'
				})
				this.map.on('mouseleave', 'geojson-fill', () => {
					if (this.map) this.map.getCanvas().style.cursor = ''
				})

				this.layersAdded = true
			}

			try {
				const b = boundsOf(this.state.geojson)
				if (!b.isEmpty()) this.map.fitBounds(b, { padding: 60, maxZoom: 15, duration: 0 })
			} catch {
				/* ignore */
			}
			this.map.resize()
			this.map.triggerRepaint()
		},
	},
}
</script>

<style scoped>
.walter-mapviewer {
	position: fixed;
	inset: 0;
	z-index: 9999;
	background: rgba(1, 57, 94, 0.55);
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
}
.walter-mapviewer__panel {
	position: relative;
	width: 100%;
	height: 100%;
	max-width: 1200px;
	background: #f1f5f8;
	border-radius: 10px;
	overflow: hidden;
	box-shadow: 0 8px 32px rgba(1, 57, 94, 0.35);
}
.walter-mapviewer__canvas {
	width: 100%;
	height: 100%;
}
.walter-mapviewer__close {
	position: absolute;
	top: 12px;
	right: 12px;
	z-index: 10001;
	padding: 6px 12px;
	border-radius: 4px;
	border: 1px solid #ccd7df;
	background: #ffffff;
	color: #01395e;
	font-size: 12px;
	cursor: pointer;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}
.walter-mapviewer__legend {
	position: absolute;
	right: 12px;
	bottom: 12px;
	z-index: 10001;
	min-width: 140px;
	padding: 8px 10px;
	background: rgba(254, 254, 254, 0.94);
	border: 1px solid #ccd7df;
	border-radius: 6px;
	box-shadow: 0 1px 4px rgba(1, 57, 94, 0.16);
	font-family: 'Open Sans', sans-serif;
	font-size: 12px;
	color: #272728;
}
.walter-mapviewer__legend-title {
	font-weight: 700;
	margin-bottom: 4px;
	white-space: nowrap;
}
.walter-mapviewer__legend-bar {
	height: 8px;
	border-radius: 2px;
	background: linear-gradient(to right, #dc2626, #f97316, #eab308, #16a34a, #15803d);
}
.walter-mapviewer__legend-scale {
	display: flex;
	justify-content: space-between;
	margin-top: 2px;
}
</style>
